/**
 * What a device reports about its setup, on `registerMyDevice` /
 * `heartbeatMyDevice` (`permissions` + `setup`, both AWSJSON). The server
 * stores only what these parsers return: known keys, known values, ≤ 2 KB.
 * Android 133 / iOS 98 and older send neither field.
 */

import { isSetupStepId, type SetupStepId } from './requirements'

export const DEVICE_REPORT_VERSION = 1
/** Larger payloads are dropped (soft-logged), never stored and never fatal. */
export const DEVICE_REPORT_MAX_BYTES = 2048

export const PERM_STATES = ['granted', 'denied', 'prompt', 'unsupported', 'unknown'] as const
export type PermState = (typeof PERM_STATES)[number]

export type LocationPrecision = 'precise' | 'approximate' | 'unknown'
export type PushTokenReport = 'registered' | 'missing' | 'error'
export type VoipTokenReport = 'registered' | 'missing' | 'unsupported'
export type WebPushReport = 'subscribed' | 'unsubscribed' | 'unsupported'

export type DevicePermissionReport = {
  v: 1
  notifications: PermState
  microphone: PermState
  camera: PermState
  location: PermState
  locationPrecise?: LocationPrecision
  /** Android 14+ full-screen call alerts. */
  fullScreenIntent?: PermState
  /** Android battery-optimisation exemption. */
  batteryExempt?: PermState
  /** Android "Incoming calls" notification channel enabled. */
  callsChannel?: PermState
  pushToken?: PushTokenReport
  /** iOS PushKit token (calls ring when the app is closed). */
  voipToken?: VoipTokenReport
  webPush?: WebPushReport
}

export type DeviceSetupReport = {
  v: 1
  flowVersion: number
  /** When first-run setup was finished on this device (local), or null. */
  completedAt: string | null
  /** Required steps not done on this device for this person. */
  outstanding: SetupStepId[]
  /** Optional steps the person said "Not now" to. */
  skipped: SetupStepId[]
  appBuild: string
}

const LOCATION_PRECISION = ['precise', 'approximate', 'unknown'] as const
const PUSH_TOKEN = ['registered', 'missing', 'error'] as const
const VOIP_TOKEN = ['registered', 'missing', 'unsupported'] as const
const WEB_PUSH = ['subscribed', 'unsubscribed', 'unsupported'] as const

function oneOf<T extends string>(allowed: readonly T[], value: unknown): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined
}

/** AWSJSON reaches a Lambda as a string; accept either, enforcing the size cap. */
function parseReportObject(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null
  let text: string
  let value: unknown = raw
  if (typeof raw === 'string') {
    text = raw
    if (byteLength(text) > DEVICE_REPORT_MAX_BYTES) return null
    try {
      value = JSON.parse(raw)
    } catch {
      return null
    }
  } else {
    try {
      text = JSON.stringify(raw) ?? ''
    } catch {
      return null
    }
    if (byteLength(text) > DEVICE_REPORT_MAX_BYTES) return null
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

/** UTF-8 byte length without TextEncoder (absent in some test environments). */
function byteLength(text: string): number {
  if (text.length > DEVICE_REPORT_MAX_BYTES) return text.length
  let bytes = 0
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i)
    if (code < 0x80) bytes += 1
    else if (code < 0x800) bytes += 2
    else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4
      i += 1
    } else bytes += 3
  }
  return bytes
}

export function normalizeDevicePermissionReport(raw: unknown): DevicePermissionReport | null {
  const obj = parseReportObject(raw)
  if (!obj || obj.v !== DEVICE_REPORT_VERSION) return null
  const perm = (key: string): PermState => oneOf(PERM_STATES, obj[key]) ?? 'unknown'
  const report: DevicePermissionReport = {
    v: 1,
    notifications: perm('notifications'),
    microphone: perm('microphone'),
    camera: perm('camera'),
    location: perm('location'),
  }
  const locationPrecise = oneOf(LOCATION_PRECISION, obj.locationPrecise)
  if (locationPrecise) report.locationPrecise = locationPrecise
  for (const key of ['fullScreenIntent', 'batteryExempt', 'callsChannel'] as const) {
    const value = oneOf(PERM_STATES, obj[key])
    if (value) report[key] = value
  }
  const pushToken = oneOf(PUSH_TOKEN, obj.pushToken)
  if (pushToken) report.pushToken = pushToken
  const voipToken = oneOf(VOIP_TOKEN, obj.voipToken)
  if (voipToken) report.voipToken = voipToken
  const webPush = oneOf(WEB_PUSH, obj.webPush)
  if (webPush) report.webPush = webPush
  return report
}

const MAX_FLOW_VERSION = 1000
const MAX_APP_BUILD_LENGTH = 40

function stepList(value: unknown): SetupStepId[] {
  if (!Array.isArray(value)) return []
  const seen = new Set<SetupStepId>()
  for (const item of value) {
    if (isSetupStepId(item)) seen.add(item)
  }
  return [...seen]
}

/** A device clock may be off; a completion more than a day in the future is dropped. */
const MAX_FUTURE_SKEW_MS = 24 * 60 * 60 * 1000

function isoOrNull(value: unknown, nowMs: number): string | null {
  if (typeof value !== 'string' || value.length > 40) return null
  const ms = Date.parse(value)
  if (!Number.isFinite(ms) || ms > nowMs + MAX_FUTURE_SKEW_MS) return null
  return new Date(ms).toISOString()
}

export function normalizeDeviceSetupReport(
  raw: unknown,
  nowMs: number = Date.now(),
): DeviceSetupReport | null {
  const obj = parseReportObject(raw)
  if (!obj || obj.v !== DEVICE_REPORT_VERSION) return null
  const flowVersion = obj.flowVersion
  if (
    typeof flowVersion !== 'number' ||
    !Number.isInteger(flowVersion) ||
    flowVersion < 1 ||
    flowVersion > MAX_FLOW_VERSION
  ) {
    return null
  }
  const appBuild =
    typeof obj.appBuild === 'string'
      ? obj.appBuild.replace(/[^\w.\-+ ()/]/g, '').slice(0, MAX_APP_BUILD_LENGTH)
      : ''
  return {
    v: 1,
    flowVersion,
    completedAt: isoOrNull(obj.completedAt, nowMs),
    outstanding: stepList(obj.outstanding),
    skipped: stepList(obj.skipped),
    appBuild,
  }
}

/** Stable fingerprint for "did the report change?" (client throttle). */
export function deviceReportFingerprint(
  permissions: DevicePermissionReport | null,
  setup: DeviceSetupReport | null,
): string {
  const sortKeys = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(sortKeys)
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.keys(value as Record<string, unknown>)
          .sort()
          .map((k) => [k, sortKeys((value as Record<string, unknown>)[k])]),
      )
    }
    return value
  }
  return JSON.stringify(sortKeys({ permissions, setup }))
}
